import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  type ButtonProps,
  composeRenderProps,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';

import { clsxMerge } from '../common/utils/classNameUtils';
import {
  Description,
  FieldError,
  FieldGroup,
  fieldStyles,
  Input,
  Label,
} from '../Field/Field';

export interface PasswordFieldProps
  extends Omit<AriaTextFieldProps, 'children' | 'type'> {
  label?: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  inputStyles?: string;
  showValidation?: boolean;
  showValidationOnFocus?: boolean;
}

interface PasswordToggleButtonProps extends ButtonProps {
  isVisible: boolean;
  onToggle: () => void;
}

function PasswordToggleButton({
  isVisible,
  onToggle,
  ...props
}: PasswordToggleButtonProps) {
  return (
    <Button
      {...props}
      onPress={onToggle}
      className="rounded-md focus:outline-none z-10 absolute right-2"
      aria-label={isVisible ? 'Hide password' : 'Show password'}
    >
      {isVisible ? (
        <FaEyeSlash className="size-4 text-gray-500" />
      ) : (
        <FaEye className="size-4 text-gray-500" />
      )}
    </Button>
  );
}

const passwordRequirements = [
  {
    key: 'upperLower',
    label: 'Upper & lower case letters',
    validate: (pwd: string) => /[a-z]/.test(pwd) && /[A-Z]/.test(pwd),
  },
  {
    key: 'symbols',
    label: 'Symbols (!@#$)',
    validate: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  },
  {
    key: 'number',
    label: 'A number',
    validate: (pwd: string) => /\d/.test(pwd),
  },
  {
    key: 'length',
    label: 'At least 8 characters',
    validate: (pwd: string) => pwd.length >= 8,
  },
];

interface PasswordValidationProps {
  password: string;
  isVisible: boolean;
}

function PasswordValidation({ password, isVisible }: PasswordValidationProps) {
  const requirements = useMemo(() => {
    return passwordRequirements.map((req) => ({
      ...req,
      met: req.validate(password),
    }));
  }, [password]);

  return (
    <div
      className={clsxMerge(
        'absolute z-50 bg-white shadow-lg border-1 rounded-lg',
        'p-2 space-y-1 w-full lg:p-4 lg:space-y-2 lg:w-64',
        'transition-all duration-300 ease-out transform',
        'lg:top-0 lg:left-full lg:ml-4 lg:origin-left',
        'top-full left-0 mt-2 origin-top lg:mt-0',
        isVisible
          ? 'opacity-100 scale-100'
          : 'opacity-0 scale-95 pointer-events-none',
      )}
      style={{
        transitionProperty: 'opacity, transform',
        transitionTimingFunction: isVisible
          ? 'cubic-bezier(0.16, 1, 0.3, 1)'
          : 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <p className="text-xs lg:text-sm text-gray-700">Must include:</p>
      <ul className="space-y-1">
        {requirements.map((req) => (
          <li
            key={req.key}
            className="flex items-center gap-2 text-xs lg:text-sm"
          >
            <div
              className={clsxMerge(
                'flex items-center justify-center size-4 rounded-full border-1 border',
                'transition-all duration-300 ease-in-out transform',
                req.met
                  ? 'bg-blue-500 border-blue-500 text-white scale-110'
                  : 'border-gray-300 text-gray-300 scale-100',
              )}
            >
              <FaCheck
                className={clsxMerge(
                  'size-2 transition-all duration-200 ease-in-out',
                  req.met ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
                )}
              />
            </div>
            <span
              className={clsxMerge(
                'transition-colors duration-200 ease-in-out',
                req.met ? 'text-gray-900' : 'text-gray-500',
              )}
            >
              {req.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PasswordField({
  label,
  description,
  isInvalid,
  errorMessage,
  isRequired,
  placeholder,
  inputStyles,
  showValidation = false,
  showValidationOnFocus = false,
  ...props
}: PasswordFieldProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [password, setPassword] = useState(props.value || '');
  const [showValidationPanel, setShowValidationPanel] =
    useState(showValidation);

  const allRequirementsMet = useMemo(() => {
    return passwordRequirements.every((req) => req.validate(password));
  }, [password]);

  useEffect(() => {
    if (allRequirementsMet && showValidationPanel) {
      const timer = setTimeout(() => {
        setShowValidationPanel(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return;
  }, [allRequirementsMet, showValidationPanel]);

  return (
    <AriaTextField
      {...props}
      isInvalid={isInvalid}
      validationBehavior="aria"
      isRequired={isRequired}
      className={composeRenderProps(
        clsxMerge(props.className),
        (customStyles, renderProps) =>
          clsxMerge(fieldStyles({ ...renderProps }), customStyles),
      )}
      onChange={(value) => {
        setPassword(value);
        props.onChange?.(value);
      }}
    >
      {label && (
        <Label isRequired={isRequired} className="flex justify-between w-full">
          {label}
          {errorMessage && (
            <span className="lg:hidden text-text-error text-xs">
              {errorMessage}
            </span>
          )}
        </Label>
      )}
      <div className="relative">
        <FieldGroup className="py-1">
          <Input
            type={isPasswordVisible ? 'text' : 'password'}
            placeholder={placeholder}
            value={password}
            className={clsxMerge('pl-2 border-0 leading-tight', inputStyles)}
            onFocus={() => {
              if (showValidationOnFocus) {
                setShowValidationPanel(true);
              } else {
                setShowValidationPanel(false);
              }
            }}
            onBlur={() => {
              if (showValidationOnFocus) {
                setShowValidationPanel(
                  !allRequirementsMet && password.length > 0,
                );
              }
            }}
          />
          <PasswordToggleButton
            isVisible={isPasswordVisible}
            onToggle={() => setIsPasswordVisible(!isPasswordVisible)}
          />
        </FieldGroup>
        {description && !isInvalid && <Description>{description}</Description>}
        <div className="hidden lg:block">
          <FieldError absolute>{errorMessage}</FieldError>
        </div>
        <PasswordValidation
          password={password}
          isVisible={showValidationPanel}
        />
      </div>
    </AriaTextField>
  );
}
